import React from "react";
import { StudyStrip, StudyNotes, SonmiMark } from "./StudyChrome";

/**
 * Visual study 1 — The Shell.
 *
 * A quiet SONMI environment. No module sidebar. No tabs. No cards.
 * The steward is present. SONMI is ambient. Three vessels are
 * available, each named by what SONMI currently understands about
 * them — not by their software category.
 *
 * Static. Placeholder content only. Not connected to any backend.
 */

export default function Shell() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-stone-700">
      <StudyStrip current="/sonmi/study/shell" />

      {/* Steward presence — top-left corner. No brand chrome above. */}
      <header className="max-w-5xl mx-auto px-8 pt-16 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="steward-presence">
          <div className="w-9 h-9 rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500"
               style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 14 }}>
            D
          </div>
          <div className="text-[13px] text-stone-500">Damon</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-stone-400" data-testid="sonmi-ambient">
          <SonmiMark />
          <span>here, quietly</span>
        </div>
      </header>

      {/* Still surface of the day — the first thing the steward encounters. */}
      <section className="max-w-3xl mx-auto px-8 pt-24" data-testid="still-surface">
        <div
          className="text-stone-900 leading-[1.25]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "2.35rem", letterSpacing: "-0.005em" }}
        >
          It is the middle of the pay period.<br />
          Plantation is quiet this afternoon.
        </div>
        <div className="mt-6 text-[14px] text-stone-500 font-light max-w-xl">
          Two things are unfolding. Nothing is pressing.
        </div>
      </section>

      {/* Available vessels — expressed as doorways of understanding, not menu items.
          No cards. No icons. No buttons. Just names of what she understands, followed
          by a single sentence of her current understanding. */}
      <section className="max-w-3xl mx-auto px-8 mt-24 space-y-14" data-testid="available-vessels">
        <Doorway
          testId="doorway-team"
          name="The team"
          understanding="Simone is a week into her new schedule. Devon returns tomorrow. Amira has been quietly steady. The rest is settled."
        />
        <Doorway
          testId="doorway-payperiod"
          name="This pay period"
          understanding="Six days remaining. Nothing yet needs your judgment. Membership numbers will finalize on Sunday."
        />
        <Doorway
          testId="doorway-today"
          name="Today"
          understanding="Two therapists on. Three appointments this afternoon. The floor is running on its own."
        />
      </section>

      {/* Deliberately: no CTA row, no primary button, no "get started". Silence is
          a first-class element of the composition. */}

      <StudyNotes
        testing="Whether an environment can feel *inhabited* rather than *navigated* — whether the steward can experience the presence of understanding without being handed modules, tabs, or a dashboard of numbers."
        understandFirst="Where the day currently is. That nothing is pressing. That two things are underway."
        withheld="Every module (payroll, scheduling, inventory, hiring), every KPI, every alert badge, every filter, every button, and every count-of-items. Those are downstream of understanding, not upstream."
        throughAttention="Each of the three named vessels (the team, this pay period, today) is intended to be enterable — the steward moves into whichever one draws them, and the vessel reorganizes around them rather than routing to a new page."
        principles="P22 (reduce cognitive burden, not agency) · P25 (feeling seen precedes accepting help — SONMI's opening line is a small act of recognition of the steward's context, not a task list) · P26 (restraint — no observation about any person appears here yet) · P27 (recognition is retrospective — the pay period sentence acknowledges what has already been) · P28 (steward's lived experience, not SONMI's internal state)."
      />
    </div>
  );
}

function Doorway({ name, understanding, testId }) {
  return (
    <div data-testid={testId} className="cursor-default select-text">
      <div
        className="text-stone-800"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: "1.35rem" }}
      >
        {name}
      </div>
      <div className="mt-2 text-[14.5px] text-stone-500 font-light leading-relaxed max-w-2xl">
        {understanding}
      </div>
    </div>
  );
}
